import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import { useScheduledTasks } from '../hooks/useScheduledTasks';
import { useScheduleRuns } from '../hooks/useScheduleRuns';
import { useBatchJobs } from '../hooks/useBatchJobs';
import { useBatchExecutions } from '../hooks/useBatchExecutions';
import { SchedulerRunDetailDrawer } from '../components/SchedulerRunDetailDrawer';
import { BatchExecutionDetailDrawer } from '../components/BatchExecutionDetailDrawer';
import { TriggerJobDialog } from '../components/TriggerJobDialog';
import { SchedulerCommonUsagePanel } from '../components/SchedulerCommonUsagePanel';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import type { StatusTone } from '@ui-kit';
import type {
  SchedulerRunStatus,
  BatchJobStatus,
  ScheduleRunListItem,
  BatchJobItem,
  BatchExecutionListItem,
  ApiError,
  ScheduleRunFilter,
} from '../types/schedulerMonitor';

type Tab = 'runs' | 'batch';
const PAGE_SIZE = 20;

// ─── 상태 → StatusPill 토운 ────────────────────────────────────────────────

function runStatusTone(status: SchedulerRunStatus): StatusTone {
  if (status === 'SUCCESS') return 'success';
  if (status === 'FAILED') return 'danger';
  return 'neutral';
}

function batchStatusTone(status: BatchJobStatus | string | null): StatusTone {
  if (!status) return 'neutral';
  if (status === 'COMPLETED') return 'success';
  if (status === 'FAILED' || status === 'ABANDONED') return 'danger';
  if (status === 'STARTED' || status === 'STARTING') return 'info';
  return 'neutral';
}

// ─── 날짜 포맷 ───────────────────────────────────────────────────────────────

function fmtInstant(iso: string | null | undefined): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' });
}

function fmtLocalDt(dt: string | null | undefined): string {
  if (!dt) return '-';
  return new Date(dt).toLocaleString('ko-KR');
}

// ─── Skeleton 행 ────────────────────────────────────────────────────────────

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${50 + (i * 13) % 40}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

// ─── 탭1: 스케줄 실행 이력 ───────────────────────────────────────────────────

function ScheduleRunsTab({
  onRowClick,
}: {
  onRowClick: (row: ScheduleRunListItem) => void;
}) {
  const { t } = useTranslation();

  // 필터 입력 상태
  const [taskNameInput, setTaskNameInput] = useState('');
  const [statusInput, setStatusInput] = useState<SchedulerRunStatus | ''>('');
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [page, setPage] = useState(0);

  // 적용 필터 (검색 클릭 시 반영)
  const [filters, setFilters] = useState<ScheduleRunFilter>({
    taskName: '',
    status: '',
    from: '',
    to: '',
    page: 0,
    size: PAGE_SIZE,
    sort: 'startedAt,desc',
  });

  const { data, isLoading, isError, error, refetch } = useScheduleRuns(filters);
  const { data: tasks } = useScheduledTasks();

  const handleSearch = useCallback(() => {
    setPage(0);
    setFilters({
      taskName: taskNameInput,
      status: statusInput,
      from: fromInput,
      to: toInput,
      page: 0,
      size: PAGE_SIZE,
      sort: 'startedAt,desc',
    });
  }, [taskNameInput, statusInput, fromInput, toInput]);

  const handleReset = useCallback(() => {
    setTaskNameInput('');
    setStatusInput('');
    setFromInput('');
    setToInput('');
    setPage(0);
    setFilters({ taskName: '', status: '', from: '', to: '', page: 0, size: PAGE_SIZE, sort: 'startedAt,desc' });
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const isEmpty = !isLoading && !isError && (data?.empty ?? false);
  const showTable = !isLoading && !isError && !isEmpty && !!data;

  const RUN_HEADERS = useMemo(
    () => [
      t('schedulerMonitor.runs.column.startedAt'),
      t('schedulerMonitor.runs.column.taskName'),
      t('schedulerMonitor.runs.column.status'),
      t('schedulerMonitor.runs.column.duration'),
      t('schedulerMonitor.runs.column.hostname'),
      t('schedulerMonitor.runs.column.error'),
    ],
    [t]
  );

  return (
    <div>
      {/* 등록 작업 요약 */}
      {tasks && tasks.length > 0 && (
        <div
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--spacing-4)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'var(--spacing-2)',
            alignItems: 'center',
          }}
        >
          <strong>{t('schedulerMonitor.tasks.label')}:</strong>
          {tasks.map((task) => (
            <span
              key={task.taskName}
              style={{
                padding: '2px var(--spacing-2)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-full)',
                background: 'var(--color-surface-elevated)',
                fontFamily: 'var(--font-family-mono)',
                whiteSpace: 'nowrap',
              }}
            >
              {task.taskName}
              {task.cronOrRate && (
                <span style={{ color: 'var(--color-text-muted)' }}>
                  {' '}({task.cronOrRate})
                </span>
              )}
              {task.lastStatus && (
                <span style={{ marginLeft: 4, color: task.lastStatus === 'SUCCESS' ? 'var(--color-success)' : 'var(--color-danger)' }}>
                  · {t(`schedulerMonitor.status.${task.lastStatus}`)}
                </span>
              )}
            </span>
          ))}
        </div>
      )}

      {/* 필터 바 */}
      <div
        role="search"
        aria-label={t('schedulerMonitor.tab.runs')}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-surface-muted)',
          marginBottom: 'var(--spacing-4)',
          alignItems: 'flex-end',
        }}
      >
        {/* 작업명 */}
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('schedulerMonitor.runs.filter.taskName')}
          <input
            type="text"
            value={taskNameInput}
            onChange={(e) => setTaskNameInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-sans)',
              minWidth: 150,
            }}
          />
        </label>

        {/* 상태 */}
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('schedulerMonitor.runs.filter.status')}
          <select
            value={statusInput}
            onChange={(e) => setStatusInput(e.target.value as SchedulerRunStatus | '')}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
            }}
          >
            <option value="">{t('schedulerMonitor.runs.filter.statusAll')}</option>
            <option value="SUCCESS">{t('schedulerMonitor.status.SUCCESS')}</option>
            <option value="FAILED">{t('schedulerMonitor.status.FAILED')}</option>
            <option value="SKIPPED">{t('schedulerMonitor.status.SKIPPED')}</option>
          </select>
        </label>

        {/* 기간 */}
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('schedulerMonitor.runs.filter.from')}
          <input
            type="date"
            value={fromInput}
            onChange={(e) => setFromInput(e.target.value)}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
            }}
          />
        </label>
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('schedulerMonitor.runs.filter.to')}
          <input
            type="date"
            value={toInput}
            onChange={(e) => setToInput(e.target.value)}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
            }}
          />
        </label>

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginLeft: 'auto' }}>
          <button
            type="button"
            onClick={handleSearch}
            disabled={isLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {t('schedulerMonitor.runs.button.search')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('schedulerMonitor.runs.button.reset')}
          </button>
        </div>
      </div>

      {/* 콘텐츠 영역 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* loading skeleton */}
        {isLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('schedulerMonitor.runs.loadingAriaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {RUN_HEADERS.map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} cols={RUN_HEADERS.length} />
              ))}
            </tbody>
          </table>
        )}

        {/* error */}
        {isError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('schedulerMonitor.error.loadFailed')}
              message={
                (error as unknown as ApiError)?.code === 'AUTH-403-001'
                  ? t('schedulerMonitor.error.forbidden')
                  : ((error as unknown as ApiError)?.message ?? t('schedulerMonitor.error.network'))
              }
              onRetry={() => void refetch()}
            />
          </div>
        )}

        {/* empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('schedulerMonitor.runs.empty.title')}
              description={t('schedulerMonitor.runs.empty.description')}
              action={
                <button
                  type="button"
                  onClick={handleReset}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    background: 'var(--color-surface-elevated)',
                    color: 'var(--color-text-primary)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  {t('schedulerMonitor.runs.button.reset')}
                </button>
              }
            />
          </div>
        )}

        {/* default DataTable */}
        {showTable && data && (
          <>
            <div role="region" aria-label={t('schedulerMonitor.runs.tableAriaLabel')} style={{ overflowX: 'auto' }}>
              <table
                style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
                aria-label={t('schedulerMonitor.runs.tableAriaLabel')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {RUN_HEADERS.map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.content.map((row) => (
                    <tr
                      key={row.id}
                      tabIndex={0}
                      aria-label={t('schedulerMonitor.runs.rowAriaLabel', { id: row.id })}
                      onClick={() => onRowClick(row)}
                      onKeyDown={(e) => { if (e.key === 'Enter') onRowClick(row); }}
                      style={{ cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = 'var(--color-surface-muted)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = '';
                      }}
                    >
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', whiteSpace: 'nowrap', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                        {fmtInstant(row.startedAt)}
                      </td>
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)' }}>
                        {row.taskName}
                      </td>
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <StatusPill tone={runStatusTone(row.status)}>
                          {t(`schedulerMonitor.status.${row.status}`)}
                        </StatusPill>
                      </td>
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', textAlign: 'right', fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                        {/* #635: BE NON_NULL 직렬화로 필드 생략(undefined) 시 !== null 이 통과 → TypeError (#616 버그클래스) */}
                        {row.durationMs != null ? row.durationMs.toLocaleString() : '-'}
                      </td>
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                        {row.hostname}
                      </td>
                      <td
                        style={{
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-danger)',
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={row.errorSummary ?? undefined}
                      >
                        {row.errorSummary ?? '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderTop: '1px solid var(--color-surface-border)',
                flexWrap: 'wrap',
                gap: 'var(--spacing-2)',
              }}
            >
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('schedulerMonitor.runs.pagination.showing', {
                  total: data.totalElements.toLocaleString(),
                  from: data.number * data.size + 1,
                  to: Math.min((data.number + 1) * data.size, data.totalElements),
                })}
              </span>
              <nav
                aria-label={t('schedulerMonitor.runs.pagination.nav')}
                style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}
              >
                <button
                  type="button"
                  disabled={data.first}
                  onClick={() => handlePageChange(page - 1)}
                  aria-label={t('schedulerMonitor.runs.pagination.prev')}
                  style={{
                    minWidth: 28, height: 28, padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: data.first ? 'not-allowed' : 'pointer',
                    opacity: data.first ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(data.totalPages, 10) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handlePageChange(i)}
                    aria-label={t('schedulerMonitor.runs.pagination.page', { page: i + 1 })}
                    aria-current={i === data.number ? 'page' : undefined}
                    style={{
                      minWidth: 28, height: 28, padding: '0 var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      background: i === data.number ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
                      border: `1px solid ${i === data.number ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
                      color: i === data.number ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                      fontWeight: i === data.number ? 600 : 400,
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={data.last}
                  onClick={() => handlePageChange(page + 1)}
                  aria-label={t('schedulerMonitor.runs.pagination.next')}
                  style={{
                    minWidth: 28, height: 28, padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: data.last ? 'not-allowed' : 'pointer',
                    opacity: data.last ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </nav>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── 탭2: 배치 작업 ──────────────────────────────────────────────────────────

function BatchJobsTab({
  onTrigger,
  onExecRowClick,
}: {
  onTrigger: (job: BatchJobItem) => void;
  onExecRowClick: (execId: number) => void;
}) {
  const { t } = useTranslation();
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [execPage, setExecPage] = useState(0);
  const [, setToast] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  const { data: jobs, isLoading: jobsLoading, isError: jobsError, error: jobsErr, refetch: refetchJobs } = useBatchJobs();
  const { data: executions, isLoading: execsLoading, isError: execsError, refetch: refetchExecs } = useBatchExecutions({
    jobName: selectedJob,
    page: execPage,
    size: PAGE_SIZE,
  });

  const JOB_HEADERS = useMemo(
    () => [
      t('schedulerMonitor.batch.column.jobName'),
      t('schedulerMonitor.batch.column.executionCount'),
      t('schedulerMonitor.batch.column.lastStatus'),
      t('schedulerMonitor.batch.column.lastStartTime'),
      t('schedulerMonitor.batch.column.actions'),
    ],
    [t]
  );

  const EXEC_HEADERS = useMemo(
    () => [
      t('schedulerMonitor.batch.exec.column.executionId'),
      t('schedulerMonitor.batch.exec.column.status'),
      t('schedulerMonitor.batch.exec.column.counts'),
      t('schedulerMonitor.batch.exec.column.startTime'),
      t('schedulerMonitor.batch.exec.column.endTime'),
    ],
    [t]
  );

  // 외부 toast ref
  void showToast; // used by parent indirectly

  return (
    <div>
      {/* 배치 작업 목록 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
          marginBottom: 'var(--spacing-5)',
        }}
      >
        {/* loading */}
        {jobsLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('schedulerMonitor.batch.tableAriaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {JOB_HEADERS.map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonRow key={i} cols={JOB_HEADERS.length} />
              ))}
            </tbody>
          </table>
        )}

        {/* error */}
        {jobsError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('schedulerMonitor.error.loadFailed')}
              message={(jobsErr as unknown as ApiError)?.message ?? t('schedulerMonitor.error.network')}
              onRetry={() => void refetchJobs()}
            />
          </div>
        )}

        {/* empty */}
        {!jobsLoading && !jobsError && (jobs ?? []).length === 0 && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('schedulerMonitor.batch.empty.title')}
              description={t('schedulerMonitor.batch.empty.description')}
            />
          </div>
        )}

        {/* default */}
        {!jobsLoading && !jobsError && (jobs ?? []).length > 0 && (
          <div role="region" aria-label={t('schedulerMonitor.batch.tableAriaLabel')} style={{ overflowX: 'auto' }}>
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
              aria-label={t('schedulerMonitor.batch.tableAriaLabel')}
            >
              <thead>
                <tr style={{ background: 'var(--color-surface-muted)' }}>
                  {JOB_HEADERS.map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(jobs ?? []).map((job) => (
                  <tr
                    key={job.jobName}
                    style={{
                      background: selectedJob === job.jobName ? 'var(--color-surface-muted)' : undefined,
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedJob !== job.jobName) {
                        (e.currentTarget as HTMLTableRowElement).style.background = 'var(--color-surface-muted)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedJob !== job.jobName) {
                        (e.currentTarget as HTMLTableRowElement).style.background = '';
                      }
                    }}
                  >
                    <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', fontFamily: 'var(--font-family-mono)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                      {job.jobName}
                    </td>
                    <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', textAlign: 'right', fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                      {job.executionCount.toLocaleString()}
                    </td>
                    <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                      {job.lastStatus ? (
                        <StatusPill tone={batchStatusTone(job.lastStatus)}>
                          {t(`schedulerMonitor.status.${job.lastStatus}`, { defaultValue: job.lastStatus })}
                        </StatusPill>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                      {fmtLocalDt(job.lastStartTime)}
                    </td>
                    <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: 'var(--spacing-1)', alignItems: 'center' }}>
                        {/* [실행] 버튼 — SCHEDULER_TRIGGER 필요 */}
                        {job.triggerable && (
                          <ButtonGuard requirePermission="SCHEDULER_TRIGGER">
                            {({ disabled, reason }) => (
                              <button
                                type="button"
                                disabled={disabled}
                                title={reason}
                                onClick={() => onTrigger(job)}
                                style={{
                                  padding: 'var(--spacing-1) var(--spacing-2)',
                                  background: disabled ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                                  color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                                  border: 'none',
                                  borderRadius: 'var(--radius-sm)',
                                  fontSize: 'var(--font-size-xs)',
                                  fontWeight: 600,
                                  cursor: disabled ? 'not-allowed' : 'pointer',
                                  fontFamily: 'var(--font-family-sans)',
                                  opacity: disabled ? 0.6 : 1,
                                }}
                              >
                                {t('schedulerMonitor.batch.button.trigger')}
                              </button>
                            )}
                          </ButtonGuard>
                        )}
                        {/* [이력] 버튼 */}
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedJob(job.jobName === selectedJob ? null : job.jobName);
                            setExecPage(0);
                          }}
                          style={{
                            padding: 'var(--spacing-1) var(--spacing-2)',
                            background: selectedJob === job.jobName ? 'var(--color-brand-primary)' : 'transparent',
                            color: selectedJob === job.jobName ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                            border: '1px solid var(--color-surface-border)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--font-size-xs)',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-family-sans)',
                          }}
                        >
                          {t('schedulerMonitor.batch.button.history')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 실행 이력 (이력 버튼 클릭 시 노출) */}
      {selectedJob && (
        <div
          style={{
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            background: 'var(--color-surface-background)',
          }}
        >
          <div
            style={{
              padding: 'var(--spacing-3) var(--spacing-4)',
              background: 'var(--color-surface-muted)',
              borderBottom: '1px solid var(--color-surface-border)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
            }}
          >
            {t('schedulerMonitor.batch.executions.title', { jobName: selectedJob })}
          </div>

          {/* loading */}
          {execsLoading && (
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
              aria-label={t('schedulerMonitor.batch.exec.tableAriaLabel')}
              aria-busy="true"
            >
              <thead>
                <tr style={{ background: 'var(--color-surface-muted)' }}>
                  {EXEC_HEADERS.map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 3 }).map((_, i) => (
                  <SkeletonRow key={i} cols={EXEC_HEADERS.length} />
                ))}
              </tbody>
            </table>
          )}

          {/* error */}
          {execsError && (
            <div style={{ padding: 'var(--spacing-4)' }}>
              <ErrorState
                title={t('schedulerMonitor.error.loadFailed')}
                message={t('schedulerMonitor.error.network')}
                onRetry={() => void refetchExecs()}
              />
            </div>
          )}

          {/* empty */}
          {!execsLoading && !execsError && (executions?.empty ?? false) && (
            <div style={{ padding: 'var(--spacing-5)' }}>
              <EmptyState title={t('schedulerMonitor.batch.exec.empty.title')} />
            </div>
          )}

          {/* default */}
          {!execsLoading && !execsError && executions && !executions.empty && (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
                aria-label={t('schedulerMonitor.batch.exec.tableAriaLabel')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {EXEC_HEADERS.map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {executions.content.map((exec: BatchExecutionListItem) => (
                    <tr
                      key={exec.executionId}
                      tabIndex={0}
                      onClick={() => onExecRowClick(exec.executionId)}
                      onKeyDown={(e) => { if (e.key === 'Enter') onExecRowClick(exec.executionId); }}
                      style={{ cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = 'var(--color-surface-muted)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = '';
                      }}
                    >
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                        {exec.executionId}
                      </td>
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <StatusPill tone={batchStatusTone(exec.status)}>
                          {t(`schedulerMonitor.status.${exec.status}`, { defaultValue: exec.status })}
                        </StatusPill>
                      </td>
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                        {exec.readCount}/{exec.writeCount}/{exec.skipCount}
                      </td>
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                        {fmtLocalDt(exec.startTime)}
                      </td>
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                        {fmtLocalDt(exec.endTime)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* executions 페이지네이션 */}
              {executions.totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    borderTop: '1px solid var(--color-surface-border)',
                    background: 'var(--color-surface-muted)',
                    gap: 'var(--spacing-1)',
                  }}
                >
                  {Array.from({ length: Math.min(executions.totalPages, 10) }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setExecPage(i)}
                      aria-current={i === executions.number ? 'page' : undefined}
                      style={{
                        minWidth: 28, height: 28, padding: '0 var(--spacing-2)',
                        borderRadius: 'var(--radius-sm)',
                        background: i === executions.number ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
                        border: `1px solid ${i === executions.number ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
                        color: i === executions.number ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                        fontSize: 'var(--font-size-xs)',
                        cursor: 'pointer',
                        fontWeight: i === executions.number ? 600 : 400,
                      }}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── 메인 페이지 ─────────────────────────────────────────────────────────────

/**
 * SchedulerMonitorPage — /scheduler-monitor
 * 5상태 (loading / empty / error / default / dialog) 구현.
 * ROLE_ADMIN 전용 (사이드바 노출, 403 ErrorState).
 */
export function SchedulerMonitorPage() {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<Tab>('runs');

  // drawer 상태
  const [runDetailId, setRunDetailId] = useState<number | null>(null);
  const [execDetailId, setExecDetailId] = useState<number | null>(null);

  // trigger dialog 상태
  const [triggerJob, setTriggerJob] = useState<BatchJobItem | null>(null);

  // toast 상태
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  const handleTriggerSuccess = useCallback(
    (_executionId: number | null) => {
      setTriggerJob(null);
      showToast(t('schedulerMonitor.toast.triggered'));
    },
    [showToast, t]
  );

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('schedulerMonitor.title')}
        breadcrumb={<span>{t('schedulerMonitor.title')}</span>}
      />

      {/* 탭 */}
      <div
        role="tablist"
        aria-label={t('schedulerMonitor.title')}
        style={{
          display: 'flex',
          gap: 0,
          borderBottom: '2px solid var(--color-surface-border)',
          marginBottom: 'var(--spacing-5)',
        }}
      >
        {(['runs', 'batch'] as Tab[]).map((tab) => (
          <button
            key={tab}
            role="tab"
            aria-selected={activeTab === tab}
            tabIndex={activeTab === tab ? 0 : -1}
            type="button"
            onClick={() => setActiveTab(tab)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') {
                setActiveTab(tab === 'runs' ? 'batch' : 'runs');
              } else if (e.key === 'ArrowLeft') {
                setActiveTab(tab === 'runs' ? 'batch' : 'runs');
              }
            }}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab
                ? '2px solid var(--color-brand-primary)'
                : '2px solid transparent',
              marginBottom: -2,
              fontSize: 'var(--font-size-sm)',
              fontWeight: activeTab === tab ? 700 : 400,
              color: activeTab === tab ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
              transition: 'color 0.15s, border-color 0.15s',
            }}
          >
            {tab === 'runs'
              ? t('schedulerMonitor.tab.runs')
              : t('schedulerMonitor.tab.batch')}
          </button>
        ))}
      </div>

      {/* 탭 패널 */}
      <div role="tabpanel" aria-label={activeTab === 'runs' ? t('schedulerMonitor.tab.runs') : t('schedulerMonitor.tab.batch')}>
        {activeTab === 'runs' && (
          <ScheduleRunsTab
            onRowClick={(row) => setRunDetailId(row.id)}
          />
        )}
        {activeTab === 'batch' && (
          <BatchJobsTab
            onTrigger={(job) => setTriggerJob(job)}
            onExecRowClick={(execId) => setExecDetailId(execId)}
          />
        )}
      </div>

      {/* 공통 모듈 패널 */}
      <SchedulerCommonUsagePanel />

      {/* Drawer — 스케줄 실행 상세 */}
      {runDetailId !== null && (
        <SchedulerRunDetailDrawer
          runId={runDetailId}
          onClose={() => setRunDetailId(null)}
        />
      )}

      {/* Drawer — 배치 실행 상세 */}
      {execDetailId !== null && (
        <BatchExecutionDetailDrawer
          executionId={execDetailId}
          onClose={() => setExecDetailId(null)}
        />
      )}

      {/* Dialog — 배치 트리거 */}
      {triggerJob && (
        <TriggerJobDialog
          jobName={triggerJob.jobName}
          onSuccess={handleTriggerSuccess}
          onClose={() => setTriggerJob(null)}
        />
      )}

      {/* 토스트 */}
      {toast !== null && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}

      {/* 반응형 스타일 + pulse 키프레임 */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          table { min-width: 480px; }
        }
      `}</style>
    </div>
  );
}
